/* @flow */
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { reaction, computed } from 'mobx';
import { Link } from 'react-router-dom';
import { translate } from 'react-i18next';
import RequireForAccess from 'react-access';
import type { TranslatorProps } from 'react-i18next';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import ReactPlaceholder from 'react-placeholder';

import { isIE } from 'utils/browserDetection';
import Loading from 'components/Loading/Loading';
import DashBoardStore from 'stores/DashboardStore';
import { updateReassignOwnership } from 'permissions/asset.js';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import * as statuses from 'utils/statuses';

import WidgetActions from '../../components/WidgetActions';
import InsufficientData from '../../components/InsufficientData/InsufficientData';
import type { CommonWidgetProps } from '../../commonTypes';

import styles from './AssetAssignment.module.scss';
import NavigationService from 'services/NavigationService';

const STATIC_DATA = [
  {
    locations: 1,
    users: 30,
    projects: 1,
    status: '-',
    er_hire_end_date: null,
  },
  {
    locations: 18,
    users: 1,
    projects: 1,
    status: '-',
    er_hire_end_date: null,
  },
  {
    locations: 1,
    users: 1,
    projects: 3,
    status: '-',
    er_hire_end_date: null,
  },
];

export type AssetAssignmentProps = CommonWidgetProps;

type InjectedProps = {
  dashboardStore: DashBoardStore,
} & TranslatorProps;

@translate()
@inject('dashboardStore')
@observer
export default class AssetAssignment extends ComponentWithInjectedProps<
  AssetAssignmentProps,
  {},
  InjectedProps
> {
  static LOAD_THUMBNAIL = false;

  autoUpdateDisposer: Function;

  @computed get shouldLoadData(): boolean {
    const { size } = this.props;

    return size !== 'thumbnail' || AssetAssignment.LOAD_THUMBNAIL;
  }

  @computed get widgetData(): any[] | null {
    const { dashboardStore } = this.props;

    if (!this.shouldLoadData) {
      return STATIC_DATA;
    }

    return dashboardStore.assignments ? dashboardStore.assignments['assets-assignment'] : null;
  }

  componentDidMount() {
    const { dashboardStore } = this.props;

    this.loadWidgetData(dashboardStore.loadedTradingAccount);

    this.autoUpdateDisposer = reaction(
      () => dashboardStore.loadedTradingAccount,
      ta => {
        this.loadWidgetData(ta);
      }
    );
  }

  loadWidgetData = (tradingAccount: string) => {
    const { dashboardStore } = this.props;

    if (this.shouldLoadData) {
      dashboardStore.loadAssignments(tradingAccount);
    }
  };

  componentWillUnmount() {
    this.autoUpdateDisposer();
  }

  render(): React.Element<*> {
    const { dashboardStore, t, i18n, widgetItem, size, isFullScreen } = this.props;
    const isInteractive = size !== 'thumbnail';
    const accountId: string = dashboardStore.loadedTradingAccount;

    function reduceToMaxValue(previous, current) {
      // find max value from each assignment object
      const maxObjValue = Math.max(current.locations, current.projects, current.users);
      // return max value
      return previous > maxObjValue ? previous : maxObjValue;
    }

    // find the max value from the assignments array
    const maxValue: number = this.widgetData ? this.widgetData.reduce(reduceToMaxValue, 0) : 0;
    // declare a min value for the heat map
    const minValue: number = 7;
    // declare a dynamic range of values for the heat map
    const step1: number = 0;
    const step2: number = maxValue > minValue ? Math.floor((maxValue / 100) * 25) : 2;
    const step3: number = maxValue > minValue ? Math.floor((maxValue / 100) * 50) : 4;
    const step4: number = maxValue > minValue ? Math.floor((maxValue / 100) * 75) : 6;

    function stepColour(count) {
      switch (true) {
        case count === step1:
          return styles['step-1'];
        case count >= step1 + 1 && count <= step2:
          return styles['step-2'];
        case count >= step2 + 1 && count <= step3:
          return styles['step-3'];
        case count >= step3 + 1 && count <= step4:
          return styles['step-4'];
        case count >= step4 + 1:
          return styles['step-5'];
      }

      return styles['step-1'];
    }

    const renderCardContent = () => (
      <React.Fragment>
        <Divider />
        <CardContent className={styles['card-content']}>
          {(this.widgetData && this.widgetData[0]) || dashboardStore.loadingAssignments ? (
            this.shouldLoadData && dashboardStore.loadingAssignments ? (
              <Loading />
            ) : (
              <div className={styles['table-container']}>
                <Typography
                  variant="headline"
                  align="center"
                  className={styles['heat-map-label-x']}
                >
                  {t('general:assets.assigned_to')}
                </Typography>

                {size === 'canvas' ? (
                  <Typography
                    variant="headline"
                    align="center"
                    className={styles['heat-map-label-y']}
                  >
                    {t('general:labels.status')}
                  </Typography>
                ) : null}

                <table className={styles['heat-map']}>
                  <thead>
                    <tr>
                      {isInteractive ? <th /> : null}
                      <th>{t('general:location_plural')}</th>
                      <th>{t('general:project_plural')}</th>
                      <th>{t('general:user_plural')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* eslint-disable max-len */
                    this.widgetData.map((assignment: Object, index: number) => {
                      const assetStatus: string = assignment.status;
                      const locationCount: number = assignment.locations;
                      const projectsCount: number = assignment.projects;
                      const usersCount: number = assignment.users;
                      const locationsStatusLink = NavigationService.formulateReportViewUri(
                        '/assets',
                        {
                          page: 1,
                          filters: [
                            ...(accountId ? [{ key: 'account', values: [accountId] }] : []),
                            { key: 'has_relationship', values: ['Has Location'] },
                            { key: 'asset_status', values: [assetStatus] },
                          ],
                        }
                      );
                      const projectsStatusLink = NavigationService.formulateReportViewUri(
                        '/assets',
                        {
                          page: 1,
                          filters: [
                            ...(accountId ? [{ key: 'account', values: [accountId] }] : []),
                            { key: 'has_relationship', values: ['Has Project'] },
                            { key: 'asset_status', values: [assetStatus] },
                          ],
                        }
                      );
                      const userStatusLink = NavigationService.formulateReportViewUri('/assets', {
                        page: 1,
                        filters: [
                          ...(accountId ? [{ key: 'account', values: [accountId] }] : []),
                          { key: 'has_relationship', values: ['Has User'] },
                          { key: 'asset_status', values: [assetStatus] },
                        ],
                      });

                      return (
                        <tr key={index.toString()}>
                          {isInteractive ? (
                            <td>
                              <span>{statuses.translate(assetStatus, i18n)}</span>
                            </td>
                          ) : null}
                          <td className={stepColour(locationCount)}>
                            <ReactPlaceholder
                              ready={this.shouldLoadData}
                              type="round"
                              color="rgba(0, 0, 0, 0.2)"
                            >
                              <Link to={locationsStatusLink}>{locationCount}</Link>
                            </ReactPlaceholder>
                          </td>
                          <td className={stepColour(projectsCount)}>
                            <ReactPlaceholder
                              ready={this.shouldLoadData}
                              type="round"
                              color="rgba(0, 0, 0, 0.2)"
                            >
                              <Link to={projectsStatusLink}>{projectsCount}</Link>
                            </ReactPlaceholder>
                          </td>
                          <td className={stepColour(usersCount)}>
                            <ReactPlaceholder
                              ready={this.shouldLoadData}
                              type="round"
                              color="rgba(0, 0, 0, 0.2)"
                            >
                              <Link to={userStatusLink}>{usersCount}</Link>
                            </ReactPlaceholder>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <InsufficientData icon={size !== 'thumbnail'} />
          )}
        </CardContent>
      </React.Fragment>
    );

    return (
      <Card className={styles[size]}>
        <CardHeader
          title={t('general:dashboards.asset_assignment')}
          action={<WidgetActions widgetItem={widgetItem} size={size} isFullScreen={isFullScreen} />}
          className={styles['card-header']}
          classes={{ action: styles.cardHeaderAction }}
        />
        {(!isIE || size !== 'thumbnail') && renderCardContent()}
        {size !== 'thumbnail' && (
          <div className={styles['button-container']}>
            {this.widgetData && this.widgetData[0] && dashboardStore.loadingAssignments !== true && (
              <div className={styles['colour-legend']}>
                <ul>
                  <li>
                    <strong className={styles['step-1']} />
                    <span>{`${step1}`}</span>
                  </li>
                  <li>
                    <strong className={styles['step-2']} />
                    <span>{`${step1 + 1}–${step2}`}</span>
                  </li>
                  <li>
                    <strong className={styles['step-3']} />
                    <span>{`${step2 + 1}–${step3}`}</span>
                  </li>
                  <li>
                    <strong className={styles['step-4']} />
                    <span>{`${step3 + 1}–${step4}`}</span>
                  </li>
                  <li>
                    <strong className={styles['step-5']} />
                    <span>{`${step4 + 1}+`}</span>
                  </li>
                </ul>
              </div>
            )}
            <RequireForAccess permissions={updateReassignOwnership}>
              <Link
                to={NavigationService.formulateReportViewUri('/assets', {
                  page: 1,
                  filters: [...(accountId ? [{ key: 'account', values: [accountId] }] : [])],
                })}
              >
                <Button color="primary" variant="contained" diabled={accountId === 0}>
                  {t('general:actions.assign_asset_plural')}
                </Button>
              </Link>
            </RequireForAccess>
          </div>
        )}
      </Card>
    );
  }
}
